import { TypedDocumentNode } from '@apollo/client';
import { DocumentNode } from 'graphql';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiState, callApiQuery, UseApiSagaOptions } from 'rootSaga';
import { getDispatch } from 'store';
import { Query } from 'types/schema';

export type GQuery<K extends keyof Query> = Omit<NonNullable<Query[K]>, '__typename'>;

type QueryFunction<TData, TVariables> = (options?: UseApiSagaOptions<TData, TVariables>) => Promise<TData>;

/**
 * Dispatches a query action into the saga and manages local loading/data/error state when it mounted.
 *
 * @example
 * const { data, loading, error, refetch } = useQuerySaga(getProducts, { variables: { filter: { query: 'milk' } } });
 */
export function useQuerySaga<TData = any, TVariables = unknown>(
  gqlQueryMutate: DocumentNode | TypedDocumentNode<TData, TVariables>,
  options?: UseApiSagaOptions<TData, TVariables>,
) {
  const dispatch = getDispatch();
  const isMounted = useRef(true);
  const autoFetchInitiated = useRef(false);

  const [state, setState] = useState<ApiState<TData>>({
    data: null,
    loading: false,
    error: null,
  });

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const fetch = useCallback<QueryFunction<TData, TVariables>>(
    (fetchOptions) => {
      return new Promise<TData>((resolve, reject) => {
        setState((s) => ({ ...s, loading: true, error: null }));
        dispatch(
          callApiQuery<TData, TVariables>({
            gqlQueryMutate,
            variables: fetchOptions?.variables || ({} as TVariables),
            authentication:
              fetchOptions && Object.hasOwn(fetchOptions, 'authentication')
                ? fetchOptions.authentication
                : options && Object.hasOwn(options, 'authentication')
                  ? options?.authentication
                  : true,
            errorPolicy: fetchOptions?.errorPolicy ?? options?.errorPolicy,
            onSuccess: (data: TData) => {
              if (isMounted.current) {
                setState({ data, loading: false, error: null });
                fetchOptions?.onSuccess?.(data);
              }
              resolve(data);
            },
            onError: (error: any) => {
              if (isMounted.current) {
                setState((s) => ({ ...s, loading: false, error }));
                fetchOptions?.onError?.(error);
              }
              reject(error);
            },
          }),
        );
      });
    },
    [gqlQueryMutate, options],
  );

  useEffect(() => {
    if (!autoFetchInitiated.current) {
      autoFetchInitiated.current = true;
      fetch(options ?? {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { ...state, refetch: fetch };
}

/**
 * Dispatches a query action into the saga and manages local loading/data/error state.
 *
 * @example
 * const [fetch, { data, loading, error }] = useLazyQuerySaga(getProducts, { variables: { filter: { query: 'milk' } } });
 */

export function useLazyQuerySaga<TData = any, TVariables = unknown>(
  gqlQueryMutate: DocumentNode | TypedDocumentNode<TData, TVariables>,
  options?: UseApiSagaOptions<TData, TVariables>,
): [QueryFunction<TData, TVariables>, ApiState<TData>] {
  const dispatch = getDispatch();
  const isMounted = useRef(true);

  const [state, setState] = useState<ApiState<TData>>({
    data: null,
    loading: false,
    error: null,
  });

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const fetch = useCallback<QueryFunction<TData, TVariables>>(
    (fetchOptions) => {
      return new Promise<TData>((resolve, reject) => {
        setState((s) => ({ ...s, loading: true, error: null }));
        dispatch(
          callApiQuery<TData, TVariables>({
            gqlQueryMutate,
            variables: fetchOptions?.variables || ({} as TVariables),
            authentication:
              fetchOptions && Object.hasOwn(fetchOptions, 'authentication')
                ? fetchOptions.authentication
                : options && Object.hasOwn(options, 'authentication')
                  ? options?.authentication
                  : true,
            errorPolicy: fetchOptions?.errorPolicy ?? options?.errorPolicy,
            onSuccess: (data: TData) => {
              if (isMounted.current) {
                setState({ data, loading: false, error: null });
                fetchOptions?.onSuccess?.(data);
              }
              resolve(data);
            },
            onError: (error: any) => {
              if (isMounted.current) {
                setState((s) => ({ ...s, loading: false, error }));
                fetchOptions?.onError?.(error);
              }
              reject(error);
            },
          }),
        );
      });
    },
    [gqlQueryMutate, options],
  );
  return [fetch, state];
}
